<template>
    <div v-if="sortedOffers.length == 0">
        <h2>{{ $t("Aktuell gibt es keine Produkte in diesem Shop.")}}</h2>
    </div>
    <div v-else uk-filter="target: .js-filter; animation: fade">
        <h2 class="uk-margin-remove">{{ $t("Tickets auswählen") }}</h2>

        <div v-if="introText" :class="{'uk-margin-medium-bottom': !(settings.coupons_enabled && (settings.coupons_first || initialCouponCode)), 'uk-margin-medium-top': true}" v-html="introText">
        </div>
        <!--
            The coupon field also appears when the buyer arrived with a code in the link,
            even in a shop that collects coupons at checkout: the code is checked on arrival
            there too, and the field is where its result — found, unknown, extra products —
            is read. Without it the unlocked products would appear with nothing explaining them.
        -->
        <Coupons v-if="settings.coupons_enabled && (settings.coupons_first || initialCouponCode)" @coupon-found="init(true)" />

        <!--
            Tab bar and search share one row. The search is a pill at the end of the tabs; opening it
            swaps the tabs for the search field. The tabs must leave the DOM while searching (v-if),
            not just hide: uk-filter toggles `display` on the grid items behind Vue's back, and only
            when its controls disappear does it reset to "all" — then Vue's filtering is the only one.
        -->
        <div ref="filter" class="fw-shop-filter uk-margin" v-if="showFilterBar">
            <transition name="fw-shop-filter-swap" mode="out-in" @after-enter="afterFilterSwap">
                <form v-if="searchOpen" key="search" class="fw-shop-search" role="search"
                      @submit.prevent @keydown.esc.prevent="closeSearch">
                    <span class="fw-shop-search-icon" uk-icon="icon: search"></span>
                    <input ref="searchInput" class="fw-shop-search-input" type="search" v-model.trim="searchTerm"
                           enterkeyhint="search" autocomplete="off"
                           :placeholder="$t('Produkte durchsuchen')" :aria-label="$t('Produkte durchsuchen')">
                    <span v-if="isSearchActive" class="fw-shop-search-count" aria-live="polite">{{ searchResultLabel }}</span>
                    <button type="button" class="fw-shop-search-close" @click="closeSearch"
                            :aria-label="$t('Suche schließen')" :title="$t('Suche schließen')">
                        <span uk-icon="icon: close; ratio: .9"></span>
                    </button>
                </form>
                <ul v-else key="tabs" class="uk-subnav uk-subnav-pill uk-margin-remove fw-shop-tabs">
                    <template v-if="showTabs">
                        <li v-if="offerId" uk-filter-control=".singleview" class="uk-hidden uk-padding-remove uk-active">
                            <a href="#">...</a>
                        </li>
                        <li v-if="offerId" uk-filter-control="" class="uk-padding-remove">
                            <a href="#" @click="offerId = null">{{ $t('Alle Tickets anzeigen') }}</a>
                        </li>
                        <li v-if="!offerId" uk-filter-control="" class="uk-padding-remove">
                            <a href="#">{{ $t('Alle Arten') }}</a>
                        </li>
                        <li v-if="!offerId" :uk-filter-control="'.tag-' + category.id" v-for="category in categories">
                            <a href="#">{{ category.name }}</a>
                        </li>
                    </template>
                    <li v-if="shouldShowSearch" class="fw-shop-search-toggle">
                        <a ref="searchToggle" href="#" role="button" @click.prevent="openSearch"
                           :aria-label="$t('Produkte durchsuchen')" :title="$t('Produkte durchsuchen')">
                            <span uk-icon="icon: search; ratio: .9"></span>
                        </a>
                    </li>
                </ul>
            </transition>
        </div>

        <div class="uk-margin uk-position-relative">
            <ul :class="{['uk-child-width-1-2@s']: true, 'uk-grid-match': true, 'js-filter': true}" id="ticketgrid" uk-grid>
                <template v-for="ticket in displayedOffers" :key="ticket.id">
                    <li class="uk-width-1-1 category" v-if="!offerId && currentCategory === undefined && displayedHeadlines[ticket.category.id] === ticket.id" :class="{['tag-'+ticket.category.id]: true}">
                        <h3>{{ticket.category.name}}</h3>
                    </li>
                    <ticket :offerId="offerId" :ticket="ticket" :id="ticket.id"></ticket>
                </template>
            </ul>
            <div v-if="isSearchActive && displayedOffers.length === 0" class="fw-shop-search-empty">
                <span uk-icon="icon: search; ratio: 1.6"></span>
                <p>{{ $t("Keine Produkte gefunden") }}</p>
                <button type="button" class="uk-button uk-button-default uk-button-small" @click="searchTerm = ''; focusSearchInput()">
                    {{ $t("Suche zurücksetzen") }}
                </button>
            </div>
        </div>
        <div class="uk-margin-medium-top uk-flex uk-flex-right">
            <button class="uk-button uk-button-primary" :disabled="hasNoItems" @click="next">
                {{ $t("Weiter") }}
            </button>
        </div>

        <!-- Angeheftete Leiste nur, wenn der Shop seine eigene Seite ist.
             Eingebettet rendert sie die einbettende Seite; siehe cartPayload
             und helper/embedBridge.js. -->
        <cart
            v-if="!embedded"
            :amount="cartAmount"
            :label="cartLabel"
            :next-label="$t('Weiter')"
            @next="next"
        />
    </div>
</template>

<script>
import Ticket from "./Ticket";
import Coupons from "./Coupons.vue";
import Cart from "../Cart.vue";
import helper from "../../helper/helper";
import {normalizeSearchText} from "../../helper/search";
import {isEmbedded, sendToParent, onParentCommand} from "../../helper/embedBridge";
import {firstBy} from "thenby";

/**
 * True when the HTML shows something: text after stripping tags, or an image.
 * `&nbsp;` counts as empty.
 */
function hasVisibleText(html) {
    if (typeof html !== 'string') {
        return false;
    }
    if (/<img\b/i.test(html)) {
        return true;
    }
    return html.replace(/<[^>]*>/g, '').replace(/&nbsp;|\u00a0/gi, ' ').trim() !== '';
}

export default {
    mixins: [helper],
    props: ["settings", "initialOfferId", "preselectedCategory", "initialCouponCode"],
    components: {Ticket, Coupons, Cart},
    data: () => {
        return {
            currentCategory: undefined,
            sortedOffers: [],
            categories: {},
            offerId: null,
            searchTerm: '',
            searchOpen: false,
            refocusSearchToggle: false,
            lockedOfferId: null,
            lockedCategoryId: null,
            embedded: isEmbedded(),
            stopCartCommands: null,
        }
    },
    computed: {
        /**
         * The intro text in the shop's language.
         *
         * The backend sends it twice: `intro_text_translations` as a locale map and
         * `intro_text` as a single string. A backend that predates the map sends only
         * the string, so the map is used only when one is actually there.
         *
         * Same fallback as php-core's Helper::getFilledTranslatedValue(): the shop's
         * language first, then German, then any language with visible text. A Redactor
         * field cleared by hand holds `<p></p>` or `<p><br></p>`, a truthy string that
         * shows nothing, so "filled" means visible text, not a non-empty string.
         */
        introText() {
            const locale = this.$root.$i18n.locale;
            const single = this.settings.intro_text;

            // `intro_text` itself can be a map: an instance that ran the migration while
            // still on the old backend code passes the stored map through unfiltered, and
            // without this check the buyer reads "[object Object]".
            const map = this.settings.intro_text_translations
                || (single && typeof single === 'object' ? single : null);

            if (!map) {
                return typeof single === 'string' ? single : '';
            }

            return [map[locale], map.de, ...Object.values(map)].find(hasVisibleText) || '';
        },
        hasNoItems() {
            return Object.keys(this.store.state.selection).length === 0
                || Object.keys(this.store.state.selection).filter(id => this.store.state.selection[id].amount > 0).length === 0
        },
        isSearchActive() {
            return this.searchTerm !== ''
        },
        shouldShowSearch() {
            return !this.offerId && this.sortedOffers.length > 8
        },
        showTabs() {
            return !this.directLinkLocked && (this.categories.length > 1 || this.offerId)
        },
        showFilterBar() {
            return this.showTabs || this.shouldShowSearch
        },
        searchResultLabel() {
            const count = this.displayedOffers.length
            return `${count} ${count === 1 ? this.$t('Produkt') : this.$t('Produkte')}`
        },
        displayedOffers() {
            if (!this.isSearchActive) {
                return this.sortedOffers;
            }
            const needle = normalizeSearchText(this.searchTerm);
            return this.sortedOffers.filter(offer =>
                normalizeSearchText(offer.name + ' ' + offer.category.name).includes(needle));
        },
        displayedHeadlines() {
            const headlines = {};
            this.displayedOffers.forEach(offer => {
                if (!headlines[offer.category.id]) {
                    headlines[offer.category.id] = offer.id;
                }
            });
            return headlines;
        },
        /**
         * Stückzahl über alle gewählten Tickets — nicht die Anzahl der
         * Ticketarten. Die Leiste soll "3 Tickets" zeigen, wenn drei Stück
         * einer Art gewählt sind, nicht "1".
         */
        cartAmount() {
            return Object.values(this.store.state.selection)
                .reduce((sum, entry) => sum + (entry.amount || 0), 0)
        },
        /**
         * state.total, nicht state.price: der Coupon-Rabatt wird schon in
         * diesem Schritt verrechnet (store.updatePrice), und die Leiste soll
         * denselben Betrag zeigen wie der Warenkorb im Checkout.
         */
        cartTotal() {
            return this.store.state.total
        },
        /**
         * Was die einbettende Seite braucht, um ihre Leiste zu rendern. Als
         * eigenes computed, damit ein Watcher darauf reagieren kann: bei jeder
         * Neuberechnung entsteht ein neues Objekt, der Watcher feuert also
         * genau dann, wenn sich Menge oder Summe wirklich geändert haben.
         */
        cartPayload() {
            return {
                amount: this.cartAmount,
                total: this.cartTotal,
                totalFormatted: this.$filters.currency(this.cartTotal),
                canProceed: !this.hasNoItems,
                // Fertig übersetzt, nicht als Bausteine: embed.js kennt die
                // Sprache des Shops nicht, und eine zweite Übersetzungstabelle
                // im Theme würde bei jedem neuen Text auseinanderlaufen.
                label: this.cartLabel,
                nextLabel: this.$t('Weiter'),
            }
        },
        /** Ein Text, zwei Darstellungen — Leiste im iframe wie außerhalb. */
        cartLabel() {
            const unit = this.cartAmount === 1 ? this.$t('Ticket') : this.$t('Tickets')
            return `${this.cartAmount} ${unit} ${this.$t('für')} ${this.$filters.currency(this.cartTotal)}`
        },
        /**
         * A direct link (?oid= / ?cat=) whose target carries the backend switch
         * "hide show all tickets". While locked, the whole category subnav is
         * gone — there is no "Alle Tickets anzeigen", no "Alle Arten" and no
         * category tab, and the offers outside the locked scope never render.
         */
        directLinkLocked() {
            return this.lockedOfferId !== null || this.lockedCategoryId !== null
        },
    },
    watch: {
        cartPayload: {
            immediate: true,
            handler(payload) {
                sendToParent({cart: payload});
            },
        },
    },
    mounted() {
        this.offerId = this.initialOfferId
        this.init()
        const offerIds = Object.keys(this.store.state.offers);
        if (offerIds.length) {
            const offers = Object.values(this.store.state.offers);
            this.trackMetaPixel('ViewContent', {
                content_ids: offerIds,
                content_type: 'ticket',
                contents: offerIds.map(id => ({ id, quantity: 1 })),
            });
            this.trackGA('view_item_list', {
                item_list_name: 'Tickets',
                items: offers.map(o => ({
                    item_id: o.id,
                    item_name: o.name,
                    price: o.price,
                    currency: 'EUR',
                })),
            });
            offers.forEach(o => {
                this.trackMatomo('setEcommerceView', String(o.id), o.name, '', o.price);
            });
        }
        if (this.preselectedCategory) {
            window.setTimeout(() => {
                let elm = document.querySelector("li[uk-filter-control='.tag-" + this.preselectedCategory+"'] > a")
                if (elm) {
                    elm.click()
                }
            })
        }
        // Der Klick auf die Leiste der einbettenden Seite kommt hier an und
        // nimmt denselben Weg wie der Button darunter — inklusive Tracking.
        // hasNoItems wird trotzdem geprüft: die Elternseite ist nicht die
        // Instanz, die über die Gültigkeit der Auswahl entscheidet.
        this.stopCartCommands = onParentCommand('cartNext', () => {
            if (!this.hasNoItems) {
                this.next()
            }
        })
    },
    unmounted() {
        this.stopCartCommands?.()
        // Ohne dieses Signal bliebe die Leiste der einbettenden Seite in den
        // Folgeschritten stehen: der Shop rendert die Ticketliste dann nicht
        // mehr, und ohne Ticketliste käme kein Update mehr, das sie ausblendet.
        sendToParent({cart: {amount: 0, total: 0, totalFormatted: '', canProceed: false, label: '', nextLabel: ''}})
    },
    methods: {
        openSearch() {
            this.searchOpen = true
        },
        closeSearch() {
            this.searchTerm = ''
            this.searchOpen = false
            this.refocusSearchToggle = true
        },
        focusSearchInput() {
            this.$nextTick(() => this.$refs.searchInput?.focus())
        },
        /* The swap runs out-in, so the element to focus only exists once it has entered. */
        afterFilterSwap() {
            if (this.searchOpen) {
                this.focusSearchInput()
            } else if (this.refocusSearchToggle) {
                // Back to the toggle, so a keyboard user does not land at the top of the page.
                this.refocusSearchToggle = false
                this.$refs.searchToggle?.focus()
            }
        },
        init(onlyShowCoupons = false) {
            const offers = Object.values(this.store.state.offers);
            this.resolveDirectLinkLock(offers)
            const categories = {}
            this.sortedOffers = this.applyDirectLinkLock(offers).sort(
                firstBy((a,b) => a.category.sortOrder - b.category.sortOrder)
                    .thenBy((a,b) => a.sortOrder - b.sortOrder)
            );
            this.sortedOffers.forEach(offer => {
                categories[offer.category.id] = offer.category;
            })
            this.categories = Object.values(categories).sort((a,b) => a.sortOrder - b.sortOrder)
            // A locked direct link has no subnav to switch, so there is no
            // coupon tab to activate either — the revealed coupon offers inside
            // the locked scope simply show up in place. The optional chaining
            // also covers the (pre-existing) case of a shop with a single
            // category, where the subnav is not rendered at all.
            /* A redeemed coupon clears the search: the revealed offers must not be filtered
               away, and an active search removes the pill bar from the DOM, so the
               .tag-coupon querySelector below would find nothing. */
            if (onlyShowCoupons) {
                this.searchTerm = '';
                this.searchOpen = false;
            }
            if (onlyShowCoupons && !this.directLinkLocked) {
                this.categories.push({id: 'coupon', name: this.$t('Coupon-Tickets'), description: ""});
                this.currentCategory = 'coupon';
                setTimeout(() => {
                    document.querySelector('[uk-filter-control=".tag-coupon"] a')?.click();
                })
            }
        },

        /**
         * Reads the direct-link lock off the backend payload.
         *
         * Scope — the narrowest thing the link names: the offer when `?oid=` is
         * present, otherwise the category from `?cat=`. Trigger — the flag on
         * that target; a flagged category additionally locks the `?oid=` links
         * into it, since "no way from here to the full list" holds for its
         * products too.
         *
         * Once established the lock is never released: `updateOffers` merges
         * coupon offers into the store and re-runs init(), and no later payload
         * may hand the buyer back the full ticket list.
         */
        resolveDirectLinkLock(offers) {
            if (this.directLinkLocked) return
            const sameId = (a, b) => a !== null && a !== undefined && b !== null && b !== undefined
                && String(a) === String(b)

            const linkedOffer = offers.find(offer => sameId(offer.id, this.initialOfferId))
            if (linkedOffer && (linkedOffer.hide_show_all_link || linkedOffer.category.hideShowAllLink)) {
                this.lockedOfferId = linkedOffer.id
                return
            }
            const inLinkedCategory = offers.find(offer => sameId(offer.category.id, this.preselectedCategory))
            if (inLinkedCategory && inLinkedCategory.category.hideShowAllLink) {
                this.lockedCategoryId = inLinkedCategory.category.id
                // Keep the state coherent: with the subnav gone there is nothing
                // left that could widen a single-offer view back to its category.
                this.offerId = null
            }
        },

        /**
         * @gotcha The locked scope has to be cut out of the offer list itself,
         *         it cannot be left to UIkit: the visible selection is driven by
         *         the *active filter control* inside the subnav, so with the
         *         subnav hidden nothing is active any more and `uk-filter` shows
         *         every item — hiding the nav alone would reveal the full list
         *         instead of restricting it.
         */
        applyDirectLinkLock(offers) {
            if (this.lockedOfferId !== null) {
                return offers.filter(offer => String(offer.id) === String(this.lockedOfferId))
            }
            if (this.lockedCategoryId !== null) {
                return offers.filter(offer => String(offer.category.id) === String(this.lockedCategoryId))
            }
            return offers
        },
        next() {
            const contents = Object.keys(this.store.state.selection).map(id => ({
                id,
                name: this.store.state.selection[id].ticket.name,
                quantity: this.store.state.selection[id].amount,
                price: this.store.state.selection[id].ticket.price,
            }));
            this.trackMetaPixel('AddToCart', {
                content_ids: contents.map(c => c.id),
                contents: contents.map(c => ({ id: c.id, quantity: c.quantity })),
                content_type: 'ticket',
                num_items: contents.reduce((sum, c) => sum + c.quantity, 0),
                value: this.store.state.price,
                currency: 'EUR',
            });
            this.trackGA('add_to_cart', {
                currency: 'EUR',
                value: this.store.state.price,
                items: contents.map(c => ({
                    item_id: c.id,
                    item_name: c.name,
                    price: c.price,
                    quantity: c.quantity,
                })),
            });
            contents.forEach(c => {
                this.trackMatomo('addEcommerceItem', String(c.id), c.name, '', c.price, c.quantity);
            });
            this.trackMatomo('trackEcommerceCartUpdate', this.store.state.price);
            this.$emit("next", this.store.state.personalize && !this.store.state.onlyNoChipTicketsSelected ? 1 : 2);
        },
    },
};
</script>
